import { useState, useEffect, useCallback } from 'react';

const LOGO = 'https://static.readdy.ai/image/705f25322c737a89d377410a2840897b/562dc63b58250b7932862d714c3260fa.png';

const SLIDES = [
  {
    type: 'image' as const,
    src: 'https://d3h1y9jvvpl9bt.cloudfront.net/assets/images/610009ab-605b-40b3-a303-4e68d624ef31/clean-shot-2026-03-08-at-16-49-38-2-x-1920w.webp',
    alt: 'Vela Bay Bayshore Road Singapore',
  },
  {
    type: 'video' as const,
    src: 'https://d3h1y9jvvpl9bt.cloudfront.net/assets/videos/2cf9eec4-920a-481d-83e1-f70df3962ab4/VID-20260122-WA0088.mp4',
    alt: 'Vela Bay Video Tour',
  },
  {
    type: 'image' as const,
    src: 'https://d3h1y9jvvpl9bt.cloudfront.net/assets/images/a4a2d395-9a89-4879-bea2-ce0ec1c6b479/clean-shot-2026-03-08-at-16-51-19-2-x-1920w.webp',
    alt: 'Vela Bay Luxury Condominium',
  },
];

interface ModalProps {
  label: string;
  onClose: () => void;
}

function RequestModal({ label, onClose }: ModalProps) {
  return (
    <div
      className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl p-8 max-w-md w-full relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 cursor-pointer w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors"
        >
          <i className="ri-close-line text-xl" />
        </button>
        <h3 className="text-xl font-bold text-[#172C37] mb-1">{label}</h3>
        <p className="text-gray-500 text-sm mb-5">
          Fill in your details and our team will reach out to you shortly.
        </p>
        <div className="flex flex-col gap-3">
          <input
            placeholder="Your Name"
            className="border border-gray-200 rounded-full px-4 py-2.5 text-sm text-[#172C37] focus:outline-none focus:border-[#172C37] w-full"
          />
          <input
            placeholder="Email Address"
            type="email"
            className="border border-gray-200 rounded-full px-4 py-2.5 text-sm text-[#172C37] focus:outline-none focus:border-[#172C37] w-full"
          />
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-[#172C37] pointer-events-none">
              +65
            </span>
            <input
              placeholder="Phone Number"
              type="tel"
              className="border border-gray-200 rounded-full pl-12 pr-4 py-2.5 text-sm text-[#172C37] focus:outline-none focus:border-[#172C37] w-full"
            />
          </div>
          <button
            onClick={onClose}
            className="bg-[#172C37] text-white font-bold py-3 rounded-full hover:opacity-70 transition-opacity cursor-pointer whitespace-nowrap mt-1"
          >
            Submit Request
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Hero() {
  const [current, setCurrent] = useState(0);
  const [modal, setModal] = useState<'brochure' | 'pricelist' | null>(null);

  const next = useCallback(() => setCurrent((c) => (c + 1) % SLIDES.length), []);
  const prev = useCallback(() => setCurrent((c) => (c - 1 + SLIDES.length) % SLIDES.length), []);

  useEffect(() => {
    if (SLIDES[current].type === 'video') return undefined;
    const timer = setTimeout(next, 5500);
    return () => clearTimeout(timer);
  }, [current, next]);

  const scrollToEnquiry = () => {
    document.getElementById('enquiry-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative h-[480px] sm:h-[580px] w-full overflow-hidden">
      {/* Slides */}
      {SLIDES.map((slide, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === current ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
          }`}
        >
          {slide.type === 'image' ? (
            <img
              src={slide.src}
              alt={slide.alt}
              className="h-full w-full object-cover object-top"
              loading={i === 0 ? 'eager' : 'lazy'}
            />
          ) : (
            <video controls playsInline className="h-full w-full object-cover">
              <source src={slide.src} type="video/mp4" />
              <track kind="captions" src="data:," srcLang="en" label="English" default />
            </video>
          )}
        </div>
      ))}

      {/* Dark gradient overlay */}
      <div className="absolute inset-0 z-20 bg-gradient-to-b from-black/55 via-black/25 to-black/60 pointer-events-none" />

      {/* Top: Logo + Title */}
      <div className="absolute top-0 left-0 right-0 z-30 flex flex-col items-center text-center px-4 sm:px-6 pt-20 sm:pt-16">
        <img
          src={LOGO}
          alt="Vela Bay Bayshore Logo"
          className="h-10 sm:h-14 w-auto object-contain mb-2 sm:mb-3 drop-shadow-lg"
        />
        <h1
          className="text-3xl sm:text-5xl lg:text-[64px] font-bold text-white tracking-widest leading-tight"
          style={{ fontFamily: '"Cormorant Garamond", serif' }}
        >
          Vela Bay
        </h1>
        <p className="text-sm sm:text-lg text-white/85 mt-1 font-light tracking-widest uppercase">
          D16 &mdash; Bedok / Upper East Coast
        </p>
      </div>

      {/* Bottom CTA Buttons */}
      <div className="absolute left-0 right-0 z-30 flex items-center justify-center gap-2 sm:gap-4 flex-wrap px-4 sm:px-6 bottom-[72px] sm:bottom-[80px]">
        <button
          onClick={() => setModal('brochure')}
          className="border border-white/80 text-white text-xs sm:text-sm font-bold px-5 sm:px-8 py-2.5 sm:py-3 rounded-full hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer backdrop-blur-sm"
        >
          Download Brochure
        </button>
        <button
          onClick={() => setModal('pricelist')}
          className="border border-white/80 text-white text-xs sm:text-sm font-bold px-5 sm:px-8 py-2.5 sm:py-3 rounded-full hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer backdrop-blur-sm"
        >
          View Pricelist
        </button>
      </div>

      {/* Prev Arrow */}
      <button
        onClick={prev}
        aria-label="Previous slide"
        className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 z-30 bg-white/90 hover:bg-white text-[#172C37] rounded-full w-9 h-9 sm:w-11 sm:h-11 flex items-center justify-center transition-transform hover:scale-105 active:scale-95 cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-lg sm:text-xl" />
      </button>

      {/* Next Arrow */}
      <button
        onClick={next}
        aria-label="Next slide"
        className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 z-30 bg-white/90 hover:bg-white text-[#172C37] rounded-full w-9 h-9 sm:w-11 sm:h-11 flex items-center justify-center transition-transform hover:scale-105 active:scale-95 cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-lg sm:text-xl" />
      </button>

      {/* Slide Counter */}
      <div className="absolute bottom-4 left-4 z-30 bg-black/50 text-white text-xs px-3 py-1.5 rounded-xl font-medium tracking-wide">
        {String(current + 1).padStart(2, '0')}/{String(SLIDES.length).padStart(2, '0')}
      </div>

      {/* Dot Indicators */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            aria-label={`Slide ${i + 1}`}
            className={`rounded-full transition-all cursor-pointer ${
              i === current ? 'w-6 h-2 bg-white' : 'w-2 h-2 bg-white/50 hover:bg-white/80'
            }`}
          />
        ))}
      </div>

      {/* Register Interest button */}
      <div className="absolute bottom-4 right-4 z-30">
        <button
          onClick={scrollToEnquiry}
          className="bg-white/15 border border-white/40 text-white text-xs font-semibold px-3 sm:px-4 py-1.5 sm:py-2 rounded-full hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap backdrop-blur-sm"
        >
          Register Interest
        </button>
      </div>

      {modal && (
        <RequestModal
          label={modal === 'brochure' ? 'Download E-Brochure' : 'View Price List'}
          onClose={() => setModal(null)}
        />
      )}
    </section>
  );
}
